<template>
  <div id="qmi-detail">
    <MoleculesLoadingOverlay v-if="loading" />
    <div v-else>
      <OrganismsProductDetailBW :organism="brandWindow" />
      <OrganismsFlexibleFeatures
        v-if="flexibleFeatures.item"
        :organism="flexibleFeatures"
      />
      <OrganismsTabbedContent
        id="explore-floorplan"
        v-if="tabbedContent.defaultTabHash"
        :organism="tabbedContent"
        class="mb-20"
      >
        <template v-for="item in tabbedContent.item" :key="item.id" #[item.id]>
          <img
            class="mx-auto mb-5"
            :src="item.image.src"
            :alt="item.image.alt"
          />
          <MoleculesButtons
            :buttons="tabbedContent.button"
            :button-colors="tabbedContent.button.colors"
            class="!justify-center mx-auto"
          />
        </template>
      </OrganismsTabbedContent>
      <OrganismsGalleryGrid v-if="galleryGrid" :organism="galleryGrid" />
      <div id="video"></div>
      <div
        class="px-2 pt-24 pb-12 mx-auto bg-bc-gray view-a-slideshow"
        v-if="viewSlideshow.slideshowEmbed"
      >
        <h2
          class="mb-8 text-2xl text-center md:text-3xl lg:text-5xl theme-text-2xl"
        >
          {{ viewSlideshow.title1 }}
        </h2>
        <div class="pb-16" v-html="viewSlideshow.slideshowEmbed"></div>
        <!--<MoleculesButtons
          :buttons="viewSlideshow.buttons"
          :button-colors="viewSlideshow.buttons.buttonColors"
          class="uppercase"
        />-->
      </div>
      <OrganismsVirtualTour
        id="virtual-tour"
        v-if="virtualTour.virtualTourUrl"
        class="mx-auto my-24 bg-gray-900"
        :organism="virtualTour"
      />
      <OrganismsMapWithInfo :organism="mapWithInfo" />
      <OrganismsTextCTA
        id="cta-be-the-first"
        :organism="beTheFirst"
        class="my-16"
      />
      <OrganismsModalForm>
        <header class="mb-4">
          <h2 class="mb-4 text-4xl font-semibold text-black font-archer">
            Be the First
          </h2>
          <p v-if="!isFormSubmitted" class="text-lg text-gray-500">
            Please provide us your information and we will get back to you.
          </p>
        </header>
        <FormsHubSpotForm
          :form="{ id: '767c334f-0893-4757-8fa6-fd4b253bd691' }"
          @submit="isFormSubmitted = true"
        />
      </OrganismsModalForm>
      <OrganismsFormCTA
        :organism="formCTA"
        class="bg-gradient-to-b from-bc-gray to-white"
      />
    </div>
  </div>
</template>

<style>
#qmi-detail {
  .virtual-tour {
    h2 {
      @apply text-white;
    }
  }
  .view-a-slideshow {
    iframe {
      @apply mx-auto w-[1038px] max-w-full h-80 sm:h-[696px] overflow-hidden;
    }
    .buttons {
      @apply justify-center;
    }
  }
  .virtual-tour {
    @apply mt-0;
    h2 {
      @apply text-white;
    }
  }
  .tabbed-content {
    header {
      background-image: linear-gradient(101deg, #367ea0 0%, #4dadce 97%);
      .header-content {
        @apply max-w-[556px];
      }
    }
    .tabs-component-tabs {
      @apply mx-auto flex-col md:flex-row justify-center;

      .tabs-component-tab {
        @apply md:max-w-[190px];

        .tabs-component-tab-a {
          @apply whitespace-nowrap;
        }
      }
    }
    .tabs-component-panels {
      .buttons a {
        @apply text-[15px] uppercase font-ubuntu font-medium py-3 px-10;
      }
    }
  }
}
</style>

<script setup>
const route = useRoute();
import { useAsyncQuery } from "#imports";
import { GET_QMI } from "~/graphql/GetQMI";
// Explicit, stable key so the SSR/ISR payload is reused on hydration instead
// of refetching (the auto key hashes a reactive proxy that differs between the
// Node prerender and the browser). Key off route.params, NOT route.path:
// route.path differs by a trailing slash between the prerender and the browser,
// which would reintroduce the mismatch. See community index.vue for the story.
const { data, pending, error } = await useAsyncQuery({
  query: GET_QMI,
  variables: { uri: route.path },
  key: `qmi-detail:${route.params.location}/${route.params.community}/${route.params.home}`,
});
import { storeToRefs } from "pinia";
const breadcrumbs = useBreadcrumbs();
const { breadcrumbsList } = storeToRefs(breadcrumbs);
const modalForm = useModalForm();
const isFormSubmitted = ref(false);

const loading = pending;

const home = computed(() => {
  return data?.value?.home ?? [];
});

const computedBreadcrumbs = computed(() => {
  const crumbs = [];
  if (
    home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
      ?.title
  ) {
    crumbs.push({
      title:
        home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
          ?.title,
      link: home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails
        ?.neighborhood?.uri,
    });
    crumbs.push({
      title: "Quick Move-In Homes",
      link:
        home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
          ?.uri + "#quick-move-in",
    });
  } else {
    crumbs.push({
      title: "Quick Move-In Homes",
      link: "/our-homes/quick-move-in-homes/",
    });
  }
  crumbs.push({
    title: home?.value?.title,
  });

  return crumbs;
});

watch(computedBreadcrumbs, () => {
  breadcrumbsList.value = computedBreadcrumbs.value;
});

const brandWindow = computed(() => {
  return {
    pageType: "qmi-detail",
    collection:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
      home?.value?.collection?.collection ??
      "",
    title: home?.value?.qmiHomeDetails?.floorplan?.title ?? home?.value?.title,
    type: home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.type ?? "",
    neighborhoodTitle: home?.value?.qmiHomeDetails?.neighborhood?.title,
    neighborhoodLink: home?.value?.qmiHomeDetails?.neighborhood?.uri,
    address1: home?.value?.qmiHomeDetails?.address1,
    address2: home?.value?.qmiHomeDetails?.address2,
    city:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.city ?? "",
    state:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.state ?? "",
    zip:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.zip ?? "",
    latitude: home?.value?.qmiHomeDetails?.latitude,
    longitude: home?.value?.qmiHomeDetails?.longitude,
    bedsMin: home?.value?.qmiHomeDetails?.bedrooms,
    bathsMin: home?.value?.qmiHomeDetails?.bathrooms,
    garageMin: home?.value?.qmiHomeDetails?.garage,
    sqftFinished: home?.value?.qmiHomeDetails?.finishedSqrFt,
    price: home?.value?.qmiHomeDetails?.price,
    productStatus: home?.value?.productStatus,
    autoplaySlider: true,
    autoplayDelay: 10000,
    sliderImages: home?.value?.qmiHomeDetails?.elevations,
    button1: true,
    button1Text: "View Floorplan",
    link1: "#explore-floorplan",
    buttonColors: ["button-blue"],
  };
});

const collectionFeatures = computed(() => {
  let parsedJSON = {};
  try {
    parsedJSON = JSON.parse(data?.value?.collectionFeatures);
  } catch (e) {
    // console.log("Invalid collectionFeatures JSON:", e);
    return {};
  }
  return parsedJSON;
});

const flexibleFeatures = computed(() => {
  const collection =
    home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
    home?.value?.collection?.collection ??
    "";
  let collectionTitle = "";
  let featuresList = [];

  switch (true) {
    case collection?.toLowerCase().includes("easyhouse"):
      collectionTitle = "easyHouse®";
      featuresList = collectionFeatures?.value?.easyHouse ?? [];
      break;
    case collection?.toLowerCase().includes("deed-restricted-wc"):
      collectionTitle = "Deed Restricted wee-Cottage®";
      featuresList = collectionFeatures?.value?.deedRestrictedWC ?? [];
      break;
    case collection?.toLowerCase().includes("wee-cottage"):
      collectionTitle = "wee-Cottage®";
      featuresList = collectionFeatures?.value?.weeCottage ?? [];
      break;
    case collection?.toLowerCase().includes("limited-edition"):
      collectionTitle = "Limited Edition";
      featuresList = collectionFeatures?.value?.limitedEdition ?? [];
      break;
    case collection?.toLowerCase().includes("new-beginnings"):
      collectionTitle = "New Beginnings";
      featuresList = collectionFeatures?.value?.newBeginnings ?? [];
      break;
  }

  return {
    title1: collectionTitle + " Features",
    item: featuresList,
  };
});

const formCTA = {
  containerWidth: "full-width",
  title1: "Curious about this home?",
  description: `<p>Whether you're just getting started or ready to schedule a tour, our online team is here to help.</p><p>Call <a href="tel:303-309-0088">303-309-0088</a> or send us a message.</p>`,
  image: {
    sourceUrl: "/img/osc-seth-2.png",
  },
  hubspotFormId: "906da098-a598-4b25-99c3-14c6641a26d4",
};

const tabbedContent = computed(() => {
  const tabs = Array.isArray(home?.value?.qmiHomeDetails?.tabs)
    ? home?.value?.qmiHomeDetails?.tabs.map((tab, currentIndex, array) => {
        let tabName = tab.title;
        // let titleCount = 0;

        array.forEach(function (checkTab, checkIndex, checkArray) {
          if (tab.title === checkTab.title) {
            // titleCount++;

            if (currentIndex > checkIndex) {
              tabName = tab.title + " - Opt";
            }
          }
        });

        return {
          name: tabName,
          id: kebabCase(tabName),
          image: {
            src: getImageSrc(tab.image, "large"),
            alt: tab.image.altText,
          },
        };
      })
    : [];

  return {
    containerWidth: "default",
    title1: "Explore this floorplan",
    title2: home?.value?.qmiHomeDetails?.floorplan?.title,
    item: tabs,
    defaultTabHash: tabs[0]?.id,
    variant: "product-detail",
    button: {
      colors: ["button-yellow"],
      button1: true,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: home?.value?.qmiHomeDetails?.pdfEPub ?? false,
    },
  };
});

const galleryGrid = computed(() => {
  if (!Array.isArray(home?.value?.qmiHomeDetails?.galleryItems)) {
    return false;
  }
  return {
    gallery: home?.value?.qmiHomeDetails?.galleryItems.reduce(
      (imageArray, item) => {
        if (item.type === "image") {
          imageArray.push(item.image);
          return imageArray;
        }
      },
      [],
    ),
  };
});

const virtualTour = computed(() => {
  const collection =
    home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.collection ??
    home?.value?.collection?.collection ??
    "";
  
  // Format collection name for display
  let collectionDisplay = collection;
  if (collection?.includes("deed-restricted-wc")) {
    collectionDisplay = "Deed Restricted wee-Cottage®";
  }
  
  const collectionSpace = collectionDisplay ? collectionDisplay + " " : "";

  const showButton1 = false;

  return {
    title1: home?.value?.qmiHomeDetails?.matterportTourTitle,
    title2: "Tour this " + collectionSpace + " home",
    virtualTourEmbedType: "url",
    virtualTourUrl: home?.value?.qmiHomeDetails?.matterportTour,
    button1: showButton1,
    button1Text: "Learn More",
    link1: home?.value?.qmiHomeDetails?.floorplan?.uri,
    buttonColors: ["button-gray"],
  };
});

const viewSlideshow = computed(() => {
  const showButton1 = home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails
    ?.pdfEPub
    ? true
    : false;
  return {
    title1:
      "Step inside the " + home?.value?.qmiHomeDetails?.matterportTourTitle,
    slideshowEmbed: home?.value?.qmiHomeDetails?.virtualTour,
    buttons: {
      button1: showButton1,
      button1Text: "Get Brochure",
      button1Target: "_blank",
      link1: home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.pdfEPub,
      buttonColors: ["button-yellow"],
    },
  };
});

const mapWithInfo = computed(() => {
  return {
    markerTitle:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.title,
    title1: "Stop by for a visit",
    address1: home?.value?.qmiHomeDetails?.address1,
    address2: home?.value?.qmiHomeDetails?.address2,
    city: home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
      ?.neighborhood?.city,
    state:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.state,
    zip: home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
      ?.neighborhood?.zip,
    latitude: home?.value?.qmiHomeDetails?.latitude,
    longitude: home?.value?.qmiHomeDetails?.longitude,
    hours:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.hours,
    phone:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.phone,
    salesTeamMembers:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.saleTeamMembers,
    legal:
      home?.value?.qmiHomeDetails?.floorplan?.floorplanDetails?.neighborhood
        ?.neighborhood?.legal,
  };
});

watch(loading, async (newLoading, oldLoading) => {
  const urlHash = window.location.hash;

  if (urlHash && oldLoading && !newLoading) {
    await nextTick();
    document.querySelector(urlHash).scrollIntoView(false);
  }
});

const beTheFirst = computed(() => {
  return {
    title1: "Be the First",
    description: `<p>Be the first to know about our Colorado neighborhoods and homes, including early access to pricing, new home plans and VIP events.</p>`,
    link1: "#modal-form",
    button1: true,
    button1Text: "Get Updates",
  };
});

onMounted(() => {
  window.addEventListener(
    "hashchange",
    () => {
      if (window.location.hash === "#modal-form") {
        modalForm.openModal();
        window.location.hash = "";
      }
    },
    false,
  );

  addTriggers();
});

const addTriggers = () => {
  const modalFormTriggers = document.querySelectorAll(
    "#cta-be-the-first .button-1, #cta-be-the-first a[href='#modal-form']",
  );

  modalFormTriggers.forEach((node) => {
    // Clone & replace node to remove existing event listeners
    const clonedNode = node.cloneNode(true);
    node.parentNode.replaceChild(clonedNode, node);

    clonedNode.addEventListener("click", (event) => {
      // Add event listener
      event.preventDefault();
      modalForm.openModal();
    });
  });
};

onUpdated(() => {
  addTriggers();
});
</script>
